import { createContext, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { isPublicRoute } from 'src/compass/shared/hooks/useCasAuthenticated';

import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useNavigate } from 'react-router-dom';
import { acquireAuthToken, IContextChildren, IAuthContext } from 'src/compass/site/util';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';
import { onErrorPermissionsHandler } from 'src/compass/shared/util/onErrorHandlers';
import infoUriFromEntity from 'src/compass/shared/util/infoUriFromEntity';
import { paths } from '../routes';

// The permissions query, which also carries the user's feature flags - so saving the flags
// refetches it (useFeatureFlagsInvalidateQuery).
export const PERMISSION_LIST_URL = infoUriFromEntity('Login/Permission');

export const AuthContext = createContext<IAuthContext>({
  token: null,
  userPermissions: null,
  loadingPermission: true,
  account: null,
  impersonatingUserName: null,
  canImpersonate: false,
  isEmployee: false,
  featureFlags: {},
});

const AuthContextProvider = ({ children }: IContextChildren) => {
  const { instance, inProgress } = useMsal();
  const [token, setToken] = useState<string | null | undefined>(null);
  const [account, setAccount] = useState<any>(null);
  const [loadingPermission, setLoadingPermission] = useState(true);

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { data } = useRetrieve({
    url: PERMISSION_LIST_URL,
    enabled: !!token,
    onError: (error: AxiosError) => {
      setLoadingPermission(false);
      onErrorPermissionsHandler(error);
    },
    onSuccess: (response: any) => {
      setLoadingPermission(false);
      return response;
    },
  });

  const { authorities, impersonatingUserName, canImpersonate, isEmployee, featureFlags } =
    data || {};

  useEffect(() => {
    if (data !== undefined) {
      if (!authorities || authorities?.length === 0) navigate(paths.page401);
    }
  }, [data, authorities, navigate]);

  useEffect(() => {
    if (!token && inProgress === InteractionStatus.None) {
      const accountInfo = instance.getActiveAccount();
      setAccount(accountInfo);

      if (isPublicRoute()) {
        setToken(undefined);
      } else {
        (async () => {
          if (accountInfo) {
            const accessToken: any = await acquireAuthToken(instance, accountInfo);
            // No token means acquireAuthToken is redirecting to sign-in: leave token null so
            // CasLoader stays up until it does. undefined would read as a public route and render
            // the app with no permissions - an empty menu (15708).
            if (accessToken) setToken(accessToken);
          }
        })();
      }
    }
  }, [inProgress, instance, token, enqueueSnackbar]);

  const valueObj = useMemo(
    () => ({
      token: token ?? null,
      userPermissions: authorities || [],
      loadingPermission,
      impersonatingUserName,
      canImpersonate: !!canImpersonate,
      isEmployee: !!isEmployee,
      account,
      featureFlags: featureFlags ?? {},
    }),
    [
      token,
      impersonatingUserName,
      canImpersonate,
      isEmployee,
      authorities,
      loadingPermission,
      account,
      featureFlags,
    ]
  );

  if ((loadingPermission || !token) && account
      && token!==undefined) {
    return <CasLoader />;
  }

  return <AuthContext.Provider value={valueObj}>{children}</AuthContext.Provider>;
};

export default AuthContextProvider;
